'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { format, setDate as setNewDate } from 'date-fns';
import { DayPicker } from '@/ui/common/Pickers/DayPicker';
import { Schedules } from '@/ui/common/Schedules';
import styles from 'react-day-picker/dist/style.module.css';
import { TicketsWidget } from '../TicketsWidget/TicketsWidget';
import { scrollToId, scrollToIdWithMargin } from '@/service/utils/scrollToId';
import { IconButton } from '@/ui/common/Buttons/IconButton';
import { CloseIcon } from '@/ui/common/Icons/CloseIcon';
import { Toast } from '@/ui/common/Toast/Toast';
import { ToastScreen } from '../CartContent/core/ToastScreen';
import { MONTH_FORMAT_DATE } from '@/service/constants/dates';
import { useAvailabilityStore } from '@/service/state/Availability/useAvailabilityStore';
import { useCartStore } from '@/service/state/Cart/useCartStore';
import { isPastDay } from '@/service/utils/isPastDay';
import { newDateFromString } from '@/service/utils/getDateByTimezone';
import { usePartnerStore } from '@/service/state/Partner/usePartnerStore';
import { cn } from '@/service/utils/cn';
import { ECartActionType } from '@/service/state/Cart/domain';
import Link from 'next/link';
import { TStatuses } from '@/domain/Activities.domain';
import { useSearchParams } from 'next/navigation';

type TAvailabilityCalendarProps = {
    partnerSlug: string;
    activityId: string;
    activityName: string;
    agreement: string;
    needTicketNames: boolean;
    isUseSchedule: boolean;
    useCalendar: boolean;
    bookingTax: number;
    bookingFee: number;
    calendarStatus?: TStatuses;
    isIframeWidget?: boolean;
};

export const DAY_PICKER_STYLES = {
    ...styles,
    root: styles.root + ' min-w-full',
    month: styles.month + ' min-w-full',
    table: styles.table + ' min-w-full',
    day: styles.day + ' highlight',
};

export const STOP_PROPAGATION = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.stopPropagation();
};

export const AvailabilityCalendar = ({
    partnerSlug,
    activityId,
    activityName,
    agreement,
    needTicketNames,
    calendarStatus,
    useCalendar,
    isUseSchedule,
    bookingFee,
    bookingTax,
    isIframeWidget,
}: TAvailabilityCalendarProps) => {
    const { isLightframePage } = usePartnerStore();
    const query = useSearchParams();
    const queryDate = query.get('date');
    const querySchedule = query.get('schedule');
    const queryDuration = query.get('duration');

    const selectedDate = useAvailabilityStore((state) => state.selectedDate);
    const [pageMonth, setPageMonth] = useState(
        format(selectedDate ? newDateFromString(selectedDate) : new Date(), MONTH_FORMAT_DATE)
    );
    const loadingMonth = useAvailabilityStore((state) => state.loadingMonth);
    const loadingDay = useAvailabilityStore((state) => state.loadingDay);
    const storeActivityId = useAvailabilityStore((state) => state.activityId);
    const storePartnerSlug = useAvailabilityStore((state) => state.partnerSlug);
    const selectedSchedule = useAvailabilityStore((state) => state.selectedSchedule);
    const days = useAvailabilityStore((state) => state.days);

    const initDate = useAvailabilityStore((state) => state.initDate);
    const setDate = useAvailabilityStore((state) => state.setDate);
    const initMonth = useAvailabilityStore((state) => state.initMonth);
    const getMonth = useAvailabilityStore((state) => state.getMonth);
    const getDay = useAvailabilityStore((state) => state.getDay);
    const getSelectedMonth = useAvailabilityStore((state) => state.getSelectedMonth);
    const getSelectedDay = useAvailabilityStore((state) => state.getSelectedDay);
    const selectSchedule = useAvailabilityStore((state) => state.selectSchedule);
    const overnightsView = useAvailabilityStore((state) => state.overnightsView);
    const toggleOvernights = useAvailabilityStore((state) => state.toggleOvernights);
    const setOvernightsDuration = useAvailabilityStore((state) => state.setOvernightsDuration);
    const selectedOvernightsDuration = useAvailabilityStore(
        (state) => state.selectedOvernightsDuration
    );
    const clearSelectedSchedule = useAvailabilityStore((state) => state.clearSelectedSchedule);

    const { success } = useCartStore();

    const selectedMonth = getSelectedMonth(pageMonth);

    const selectedDay = useMemo(
        () => getSelectedDay(selectedDate),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedDate, days, overnightsView]
    );

    useEffect(() => {
        if (!selectedDate || activityId !== storeActivityId || partnerSlug !== storePartnerSlug) {
            initDate(queryDate || null, activityId, {
                partnerSlug,
                needTicketsNames: needTicketNames,
                bookingFee,
                bookingTax,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activityId, partnerSlug, queryDate]);

    useEffect(() => {
        if (querySchedule && !!+querySchedule) {
            if (queryDuration && !!+queryDuration) {
                (async () => {
                    await toggleOvernights();
                    await setOvernightsDuration(queryDuration);

                    onSelectSchedule(+querySchedule);
                })();
            } else {
                onSelectSchedule(+querySchedule);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [querySchedule, queryDuration]);

    useEffect(() => {
        if (!loadingMonth && !selectedMonth && selectedDate) {
            if (useCalendar) {
                initMonth(selectedDate, (date) => {
                    setPageMonth(format(date, MONTH_FORMAT_DATE));
                });
            }
            getDay(selectedDate, {
                noSchedules: !isUseSchedule,
                needTicketsName: needTicketNames,
                noCalendar: !useCalendar,
            });
        }
    }, [
        loadingMonth,
        selectedMonth,
        selectedDate,
        setPageMonth,
        initMonth,
        getDay,
        isUseSchedule,
        needTicketNames,
        useCalendar,
    ]);

    const onSelectDayPicker = useCallback(
        (newDate?: Date) => {
            if (newDate) {
                setDate(newDate);
                getDay(newDate, {
                    noSchedules: !isUseSchedule,
                    needTicketsName: needTicketNames,
                });
            }
            setTimeout(
                () =>
                    isLightframePage
                        ? scrollToId('schedules', 'nearest')
                        : scrollToIdWithMargin('schedules'),
                300
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isUseSchedule, needTicketNames, isLightframePage]
    );

    const isDisabled = useCallback(
        (date: Date) => isPastDay(date) || !selectedMonth?.includes(date.getDate().toString()),
        [selectedMonth]
    );

    const onMonthChange = useCallback((newDate: Date) => {
        setPageMonth(format(newDate, MONTH_FORMAT_DATE));
        getMonth(newDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const today = useMemo(() => new Date(), []);
    const selectedDateData = useMemo(() => newDateFromString(selectedDate), [selectedDate]);

    const onSelectSchedule = useCallback(
        (scheduleId: number) => {
            if (isIframeWidget) {
                window.parent.postMessage(
                    {
                        type: 'wr_open_schedule',
                        link: `/lightframe${storePartnerSlug}/details/${storeActivityId}`,
                        params: `schedule=${scheduleId}&date=${selectedDate}${selectedOvernightsDuration ? '&duration=' + selectedOvernightsDuration : ''}`,
                    },
                    '*'
                );
                return;
            }

            selectSchedule(scheduleId);
            setTimeout(() => scrollToId('tickets-widget', 'start'), 500);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            isLightframePage,
            storeActivityId,
            storePartnerSlug,
            selectedDate,
            selectedOvernightsDuration,
        ]
    );

    const nextAvailDate = selectedMonth?.[0];

    const onSelectNextAvailDate = useCallback(() => {
        if (nextAvailDate) {
            const newDate = setNewDate(newDateFromString(selectedDate), +nextAvailDate);
            if (newDate) {
                setDate(newDate);
                getDay(newDate, {
                    noSchedules: !isUseSchedule,
                    needTicketsName: needTicketNames,
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nextAvailDate, selectedDate, isUseSchedule, needTicketNames]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const selectedDayData = useMemo(() => selectedDay?.[0], [selectedDay?.[0]]);

    const isLoadingMonth = !selectedDate || (!selectedMonth && useCalendar) || loadingMonth;
    const isLoadingDay = !selectedDate || loadingDay || isLoadingMonth;
    const monthUnavail = !selectedMonth?.length;
    const notAvailDay = useMemo(
        () =>
            !selectedMonth?.includes(newDateFromString(selectedDate).getDate().toString()) &&
            useCalendar,
        [selectedMonth, selectedDate, useCalendar]
    );
    const checkoutLink = useMemo(() => `/${partnerSlug}/checkout/`, [partnerSlug]);

    return (
        <>
            <Toast
                show={!!success.message && success.actionType === ECartActionType.ADD_ITEM_TO_CART}
                screen={
                    <ToastScreen
                        title={success.message}
                        description={
                            <Link
                                href={checkoutLink}
                                className="tx-large-medium block w-full text-brand underline"
                            >
                                Checkout now
                            </Link>
                        }
                    />
                }
            />
            {useCalendar && (
                <div
                    className={cn(
                        'rounded-2xl bg-white px-4 pb-4 shadow-lightCard dark:bg-dark-3 dark:shadow-darkCard md:pb-[18px] md:pt-[2px] lg:px-[26px]',
                        isLoadingMonth ? 'loading' : ''
                    )}
                    id="calendar"
                    data-test-id="avail-calendar"
                >
                    <DayPicker
                        mode="single"
                        classNames={DAY_PICKER_STYLES}
                        selected={selectedDateData}
                        onSelect={onSelectDayPicker}
                        disabled={isDisabled}
                        onMonthChange={onMonthChange}
                        fromMonth={today}
                        today={today}
                        month={newDateFromString(pageMonth)}
                        isLoading={isLoadingMonth}
                    />
                    <div className="tx-large-medium mt-2.5 flex justify-center text-gray-1 dark:text-white md:mt-4">
                        <span className="mr-5 flex items-center before:mr-2 before:block before:h-4 before:w-4 before:rounded-2xl before:bg-brand before:opacity-70 before:content-['']">
                            Selected
                        </span>
                        <span className="mr-5 flex items-center before:mr-2 before:block before:h-4 before:w-4 before:rounded-2xl before:bg-brand-light before:opacity-70 before:content-[''] dark:before:bg-brand-dark">
                            Available
                        </span>
                    </div>
                </div>
            )}
            <Schedules
                list={selectedDayData?.schedules}
                isLoading={isLoadingDay}
                limit={10}
                selected={selectedDateData}
                selectSchedule={onSelectSchedule}
                isUseSchedule={isUseSchedule}
                isUseCalendar={useCalendar}
                selectNextAvailDay={onSelectNextAvailDate}
                monthUnavail={monthUnavail}
                notAvailDay={notAvailDay}
                hasOvernights={selectedDayData?.has_overnights}
                isIframeWidget={isIframeWidget}
            />
            {typeof selectedSchedule !== 'undefined' && (
                <div
                    className={cn(
                        'md:fixed md:bottom-0 md:left-0 md:right-0 md:top-0 md:z-50 md:flex md:cursor-pointer md:items-center md:justify-center md:bg-black md:bg-opacity-50 md:pt-16'
                    )}
                    onClick={clearSelectedSchedule}
                    id="availability"
                    data-test-id="availability"
                >
                    <div
                        className={cn(
                            'relative mt-8 w-full max-w-[810px] rounded-2xl bg-white dark:bg-blue-main md:absolute md:mt-0 md:cursor-auto',
                            isLightframePage ? 'md:max-h-[calc(90vh_-_75px)]' : 'md:max-h-[90vh]'
                        )}
                        onClick={STOP_PROPAGATION}
                    >
                        <IconButton
                            icon={<CloseIcon className="h-9 w-9 bg-ash dark:bg-blue-dark" />}
                            className="absolute right-4 top-6 hidden rounded-3xl md:block"
                            onClick={clearSelectedSchedule}
                            data-test-id="close-btn"
                        />
                        <TicketsWidget
                            calendarStatus={calendarStatus}
                            tripName={activityName}
                            agreement={agreement}
                            hideDate={!isUseSchedule && !useCalendar}
                            redirectAfterAddToCart
                        />
                    </div>
                </div>
            )}
        </>
    );
};
